import React, { useState, useEffect } from "react";
import { Table, Button, Space, Modal } from "antd"; // Import Table, Button, Space, Modal from Ant Design
import { WorkflowApi } from "../../services/WorkflowApi";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../contexts/AxiosProvider";
import { useSnackbar } from "notistack";

const { Column } = Table;

// DateConverter function
export const DateConverter = (dateString) => {
  const d = new Date(dateString);
  return d.toDateString();
};

export default function WorkflowTable() {
  const axios = useAxios();
  const [workflowData, setWorkflowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  useEffect(() => {
    fetchAllWorkflows();
  }, [page]);

  const fetchAllWorkflows = async () => {
    try {
      setLoading(true);
      const data = {
        user_id: location.state.userId,
        process_type: "data-all",
        client: false,
        client_id: "",
        search: false,
        searchParam: "",
        pageNumber: page,
        pageSize: rowsPerPage,
        status: "pending",
      };
      const response = await WorkflowApi.getAllWorkflows(axios, data);
      if (response.status === 200) {
        setWorkflowData(response.data);
      } else {
        enqueueSnackbar("Failed to fetch workflows!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } catch (error) {
      if (error.response?.status === 500) {
        enqueueSnackbar("500 Internal Server Error!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      } else {
        enqueueSnackbar("An error occurred!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (pageNumber) => {
    setPage(pageNumber);
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      const data = {
        workflow_id: selectedWorkflow.workflow_id,
        status: "approved",
      };
      const response = await WorkflowApi.workflowStatusChanged(axios, data);
      if (response.status === 200) {
        enqueueSnackbar("Workflow approved successfully !!", {
          variant: "success",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        fetchAllWorkflows();
      }
    } catch (error) {
      if (error.response?.status === 500) {
        enqueueSnackbar("500 Internal Server Error!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } finally {
      setLoading(false);
      setApproveModalVisible(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      const data = {
        workflow_id: selectedWorkflow.workflow_id,
        status: "rejected",
      };
      const response = await WorkflowApi.workflowStatusChanged(axios, data);
      if (response.status === 200) {
        enqueueSnackbar("Workflow rejected successfully !!", {
          variant: "success",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        fetchAllWorkflows();
      }
    } catch (error) {
      if (error.response?.status === 500) {
        enqueueSnackbar("500 Internal Server Error!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } finally {
      setLoading(false);
      setRejectModalVisible(false);
    }
  };

  const handleApproveModal = (record) => {
    setSelectedWorkflow(record);
    setApproveModalVisible(true);
  };

  const handleRejectModal = (record) => {
    setSelectedWorkflow(record);
    setRejectModalVisible(true);
  };

  return (
    <>
      <Table
        dataSource={workflowData}
        pagination={{
          pageSize: rowsPerPage,
          current: page,
          onChange: handleChangePage,
        }}
        loading={loading}
      >
        <Column
          title="Workflow Name"
          dataIndex="workflow_name"
          key="workflow_name"
        />
        <Column
          title="Pipeline Name"
          dataIndex="engagement_name"
          key="engagement_name"
        />
        <Column title="Created By" dataIndex="created_by" key="created_by" />
        <Column
          title="Created On"
          dataIndex="created_at"
          key="created_at"
          render={(created_at) => DateConverter(created_at)}
        />
        <Column
          title="Action"
          key="action"
          render={(text, record) => (
            <Space size="middle">
              <Button type="primary" onClick={() => handleApproveModal(record)}>
                Approve
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => handleRejectModal(record)}
              >
                Reject
              </Button>
            </Space>
          )}
        />
      </Table>

      {/* Approve Modal */}
      <Modal
        title="Approve Workflow"
        visible={approveModalVisible}
        onOk={handleApprove}
        onCancel={() => setApproveModalVisible(false)}
      >
        <p>
          Are you sure you want to approve the workflow "
          {selectedWorkflow?.workflow_name}"?
        </p>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Workflow"
        visible={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => setRejectModalVisible(false)}
      >
        <p>
          Are you sure you want to reject the workflow "
          {selectedWorkflow?.workflow_name}"?
        </p>
      </Modal>
    </>
  );
}
